import { Directive, effect, ElementRef, HostBinding, HostListener, inject, input, OnDestroy, output, signal } from '@angular/core';
import { DragAndDropService } from '@features/drag-and-drop/drag-and-drop.service';
import { IDraggable, IDropTarget } from '@features/drag-and-drop/drag-and-drop.types';
import { getUntransformedBoundingBox, Ticker } from '@shared/misc.utils';
import { addTransition, removeTransition } from '@shared/style.utils';
import { Vector2ElementNormalized, Vector2Screen } from '@typings/vectors';

@Directive({
  selector: '[appDraggable]',
})
export class DraggableDirective<T> implements IDraggable, OnDestroy {
  @HostBinding('style.pointerEvents') pointerEvents = 'all';
  @HostBinding('style.cursor') cursor = 'grab';
  readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  // INPUTS
  readonly dragContent = input<T>();
  readonly dragPivot = input<Vector2ElementNormalized>([0.5, 0.5] as Vector2ElementNormalized);
  readonly dragRoot = input<HTMLElement>();
  readonly dragMode = input<'MANUAL' | 'AUTO'>('AUTO');
  readonly disabled = input<boolean>(false);

  // OUTPUTS
  draggableChangedPosition = output<Vector2Screen>();
  draggableChangedStyle = output<Partial<CSSStyleDeclaration>>();
  draggableGrabbed = output();
  draggableTargetEntered = output<IDropTarget>();
  draggableTargetLeft = output<IDropTarget>();
  draggableDropped = output<IDropTarget>();
  draggableReleased = output();

  readonly target = signal<undefined | IDropTarget>(undefined);
  readonly #dragAndDropService = inject(DragAndDropService);

  // INTERNAL STATE
  private canDrag = signal(false);
  private isDragging = signal(false);

  private ticker: Ticker;
  private screenPositionMouse = signal<Vector2Screen>([0, 0] as Vector2Screen);

  constructor() {
    this.registerGrabCursor();
    this.#dragAndDropService.registerDraggable(this);
    this.ticker = new Ticker(() => {
      const target = this.target();
      if (this.isDragging()) {
        this.#dragAndDropService.drag(this, this.screenPositionMouse());
      } else if (target) {
        this.#dragAndDropService.translateToTarget(this, target);
      }
    });
  }

  // DESTRUCTION
  ngOnDestroy(): void {
    this.#dragAndDropService.unregisterDraggable(this);
    this.ticker.stop();
  }

  // SERVICE INTERFACE
  public setTarget(target: IDropTarget | undefined): void {
    this.target.set(target);
    this.draggableReleased.emit();
    if (target) {
      this.drop(target);
    } else {
      if (this.dragMode() === 'AUTO') {
        this.el.nativeElement.style.transition = addTransition(this.el.nativeElement.style.transition, 'translate 0.1s ease-out');
      }
      this.draggableChangedStyle.emit({
        transition: 'translate 0.1s ease-out',
      });
    }
  }

  public getPivotWithoutTransform(): Vector2Screen {
    const rect = getUntransformedBoundingBox(this.dragRoot() ?? this.el.nativeElement);
    const pivot = [rect.x + this.dragPivot()[0] * rect.width, rect.y + this.dragPivot()[1] * rect.height] as Vector2Screen;
    return pivot;
  }

  public setTranslation(translation: Vector2Screen): void {
    if (this.dragMode() === 'AUTO') {
      this.el.nativeElement.style.translate = `${translation[0]}px ${translation[1]}px`;
    }
    this.draggableChangedPosition.emit(translation);
  }

  public setZIndex(zIndex: number): void {
    if (this.dragMode() === 'AUTO') {
      this.el.nativeElement.style.zIndex = zIndex?.toString() ?? 'unset';
    }
    this.draggableChangedStyle.emit({ zIndex: `${zIndex}` });
  }

  public entered(target: IDropTarget): void {
    this.draggableTargetEntered.emit(target);
  }

  public exited(target: IDropTarget): void {
    this.draggableTargetLeft.emit(target);
  }

  public getContent(): T {
    return this.dragContent() as T;
  }

  @HostListener('pointerdown', ['$event'])
  handlePointerDown(event: PointerEvent) {
    if (this.disabled()) return; // <-- Early return if disabled
    this.onDragStart(event.x, event.y, () => event.stopPropagation());
  }

  @HostListener('document:pointermove', ['$event'])
  handlePointerMove(event: PointerEvent) {
    if (this.disabled()) return; // <-- Early return if disabled
    this.onDragMove(event.x, event.y);
  }

  @HostListener('document:pointerup', ['$event'])
  handlePointerUp(event: PointerEvent) {
    if (this.disabled()) return; // <-- Early return if disabled
    this.onDragEnd(event.x, event.y);
  }

  @HostListener('touchstart', ['$event'])
  handleTouchStart(event: TouchEvent) {
    if (this.disabled()) return; // <-- Early return if disabled
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      this.onDragStart(touch.clientX, touch.clientY, () => event.stopPropagation());
    }
  }

  @HostListener('document:touchmove', ['$event'])
  handleTouchMove(event: TouchEvent) {
    if (this.disabled()) return; // <-- Early return if disabled
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      this.onDragMove(touch.clientX, touch.clientY);
    }
  }

  @HostListener('document:touchend', ['$event'])
  handleTouchEnd(event: TouchEvent) {
    if (this.disabled()) return; // <-- Early return if disabled
    if (event.changedTouches.length > 0) {
      const touch = event.changedTouches[0];
      this.onDragEnd(touch.clientX, touch.clientY);
    }
  }

  // INTERNAL METHODS
  protected grab(position: Vector2Screen) {
    this.isDragging.set(true);
    this.#dragAndDropService.grab(this, position);
    this.draggableGrabbed.emit();
    if (this.dragMode() === 'AUTO') {
      this.el.nativeElement.style.transition = addTransition(this.el.nativeElement.style.transition, 'translate 0.1s ease-out');
    }
    this.draggableChangedStyle.emit({
      transition: 'translate 0.1s ease-out',
    });
  }

  protected drag(position: Vector2Screen) {
    this.#dragAndDropService.drag(this, position);
  }

  protected drop(target: IDropTarget) {
    this.draggableDropped.emit(target);
    if (this.dragMode() === 'AUTO') {
      this.el.nativeElement.style.transition = removeTransition(this.el.nativeElement.style.transition, 'translate');
    }
    this.draggableChangedStyle.emit({
      transition: 'translate 0s',
    });
  }

  protected release(position: Vector2Screen) {
    this.#dragAndDropService.release(this, position);
    this.isDragging.set(false);
  }

  // INITIALIZATION
  private registerGrabCursor() {
    effect(() => {
      this.cursor = this.isDragging() ? 'grabbing' : 'grab';
    });
  }

  // EVENT HANDLERS
  private onDragStart(x: number, y: number, stopPropagation?: () => void) {
    this.canDrag.set(true);
    if (stopPropagation) stopPropagation();
  }

  private onDragMove(x: number, y: number) {
    this.screenPositionMouse.set([x, y] as Vector2Screen);

    if (!this.canDrag()) return;

    if (!this.isDragging()) {
      this.grab([x, y] as Vector2Screen);
    } else {
      this.drag([x, y] as Vector2Screen);
    }
  }

  private onDragEnd(x: number, y: number) {
    if (this.isDragging()) {
      this.release([x, y] as Vector2Screen);
    }
    this.canDrag.set(false);
  }
}
